import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Map, Grid3X3 } from 'lucide-react';
import { PlotMapEditor } from './PlotMapEditor';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  available: { bg: 'bg-green-100 hover:bg-green-200 border-green-300', text: 'text-green-800', label: 'Available' },
  booked: { bg: 'bg-yellow-100 hover:bg-yellow-200 border-yellow-300', text: 'text-yellow-800', label: 'Booked' },
  confirmed: { bg: 'bg-blue-100 hover:bg-blue-200 border-blue-300', text: 'text-blue-800', label: 'Confirmed' },
  transferred: { bg: 'bg-red-100 hover:bg-red-200 border-red-300', text: 'text-red-800', label: 'Sold' },
  cancelled: { bg: 'bg-gray-100 hover:bg-gray-200 border-gray-300', text: 'text-gray-600', label: 'Cancelled' },
};

export function PlotMapping() {
  const { user } = useAuth();
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedCell, setSelectedCell] = useState<any>(null);

  const { data: projects } = useQuery({
    queryKey: ['my-projects-map', user?.id],
    queryFn: async () => {
      const [{ data: devProfile }, { data: myAgency }] = await Promise.all([
        supabase.from('developer_profiles').select('id').eq('user_id', user!.id).maybeSingle(),
        supabase.from('agencies').select('id').eq('created_by', user!.id).maybeSingle(),
      ]);
      const orParts: string[] = [];
      if (devProfile?.id) orParts.push(`developer_id.eq.${devProfile.id}`);
      if (myAgency?.id) orParts.push(`marketing_agency_id.eq.${myAgency.id}`);
      if (orParts.length === 0) return [];
      const { data } = await supabase.from('projects').select('id, name, city, total_units').or(orParts.join(','));
      return data || [];
    },
    enabled: !!user,
  });

  const { data: unitTypes } = useQuery({
    queryKey: ['unit-types-map', selectedProject],
    queryFn: async () => {
      const { data } = await supabase.from('project_unit_types').select('*').eq('project_id', selectedProject).order('sort_order');
      return data || [];
    },
    enabled: !!selectedProject,
  });

  const { data: bookings } = useQuery({
    queryKey: ['bookings-map', selectedProject],
    queryFn: async () => {
      const { data } = await supabase
        .from('bookings')
        .select('*, project_unit_types(name)')
        .eq('project_id', selectedProject)
        .eq('developer_user_id', user!.id);
      return data || [];
    },
    enabled: !!selectedProject && !!user,
  });

  const project = projects?.find((p: any) => p.id === selectedProject);
  const totalUnits = project?.total_units || 0;

  // Build grid: for each unit slot, check if there's a booking
  const buildGrid = () => {
    const cells: any[] = [];
    const bookingsByPlot: Record<string, any> = {};
    (bookings || []).forEach((b: any) => {
      if (b.plot_number) bookingsByPlot[b.plot_number] = b;
    });

    const count = Math.max(totalUnits, (bookings || []).length, 12);
    for (let i = 1; i <= count; i++) {
      const plotNum = String(i);
      const booking = bookingsByPlot[plotNum];
      cells.push({
        number: plotNum,
        status: booking ? booking.booking_status : 'available',
        booking,
      });
    }
    return cells;
  };

  const cells = selectedProject ? buildGrid() : [];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <Map className="h-5 w-5 text-primary" /> Plot / Unit Map
        </h2>
        <p className="text-sm text-muted-foreground">Visual overview of unit availability per project.</p>
      </div>

      <div className="flex items-center gap-3">
        <Select value={selectedProject} onValueChange={setSelectedProject}>
          <SelectTrigger className="w-[280px] h-9 text-sm">
            <SelectValue placeholder="Select a project" />
          </SelectTrigger>
          <SelectContent>
            {(projects || []).map((p: any) => (
              <SelectItem key={p.id} value={p.id}>
                {typeof p.name === 'object' ? (p.name as any)?.en : p.name} — {p.city}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Legend */}
        <div className="flex gap-3 text-xs ml-auto">
          {Object.entries(STATUS_COLORS).filter(([k]) => k !== 'cancelled').map(([key, val]) => (
            <div key={key} className="flex items-center gap-1">
              <div className={`h-3 w-3 rounded ${val.bg} border`} />
              <span className="text-muted-foreground">{val.label}</span>
            </div>
          ))}
        </div>
      </div>

      {!selectedProject ? (
        <div className="text-center py-16 text-muted-foreground text-sm">
          <Grid3X3 className="h-12 w-12 mx-auto mb-3 opacity-30" />
          Select a project to view the unit map.
        </div>
      ) : (
        <Tabs defaultValue="map" className="w-full">
          <TabsList>
            <TabsTrigger value="map">Map View</TabsTrigger>
            <TabsTrigger value="grid">Unit Grid</TabsTrigger>
          </TabsList>
          <TabsContent value="map" className="mt-4">
            <PlotMapEditor projectId={selectedProject} />
          </TabsContent>
          <TabsContent value="grid" className="mt-4">
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-2">
              {cells.map(cell => {
                const config = STATUS_COLORS[cell.status] || STATUS_COLORS.available;
                return (
                  <button
                    key={cell.number}
                    onClick={() => setSelectedCell(cell)}
                    className={`aspect-square rounded-lg border ${config.bg} flex flex-col items-center justify-center transition-colors cursor-pointer`}
                  >
                    <span className={`text-xs font-bold ${config.text}`}>{cell.number}</span>
                    <span className={`text-[8px] ${config.text} opacity-70`}>{config.label}</span>
                  </button>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      )}

      {/* Cell Detail Dialog */}
      <Dialog open={!!selectedCell} onOpenChange={(open) => !open && setSelectedCell(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Unit #{selectedCell?.number}</DialogTitle>
          </DialogHeader>
          {selectedCell && (
            <div className="space-y-3">
              <Badge className={STATUS_COLORS[selectedCell.status]?.bg || ''}>
                {STATUS_COLORS[selectedCell.status]?.label || selectedCell.status}
              </Badge>
              {selectedCell.booking ? (
                <div className="space-y-2 text-sm">
                  <div><span className="text-muted-foreground">Customer:</span> <strong>{selectedCell.booking.customer_name}</strong></div>
                  <div><span className="text-muted-foreground">Phone:</span> {selectedCell.booking.customer_phone}</div>
                  {selectedCell.booking.customer_email && (
                    <div><span className="text-muted-foreground">Email:</span> {selectedCell.booking.customer_email}</div>
                  )}
                  {selectedCell.booking.booking_amount && (
                    <div><span className="text-muted-foreground">Amount:</span> {Number(selectedCell.booking.booking_amount).toLocaleString()} ETB</div>
                  )}
                  <div><span className="text-muted-foreground">Date:</span> {new Date(selectedCell.booking.booking_date).toLocaleDateString()}</div>
                  {selectedCell.booking.project_unit_types?.name && (
                    <div><span className="text-muted-foreground">Type:</span> {selectedCell.booking.project_unit_types.name}</div>
                  )}
                  {selectedCell.booking.notes && (
                    <div><span className="text-muted-foreground">Notes:</span> {selectedCell.booking.notes}</div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">This unit is available. Create a booking from the Bookings page.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
